import { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import { alpha, useTheme } from '@mui/material/styles';

import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Word-style font choices. Each value is a real font family name that the
// browser can resolve via system fonts; no web-font fetch required.
const FONT_WHITELIST = [
  'Arial',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Comic Sans MS',
  'Calibri'
];

// Discrete px steps for the size dropdown — also doubles as the valid range
// for the ±1 buttons (clamped to first/last).
const SIZE_WHITELIST = [];
for (let px = 8; px <= 48; px += 1) SIZE_WHITELIST.push(`${px}px`);

// Register the style-based attributors once at module load. Style-based (vs.
// class-based) means the inline `style="font-family: …"` lives on the spans,
// so the formatting survives a copy/paste out of the editor and renders
// correctly in the read-only view + PDF.
const FontAttributor = Quill.import('attributors/style/font');
FontAttributor.whitelist = FONT_WHITELIST;
Quill.register(FontAttributor, true);

const SizeAttributor = Quill.import('attributors/style/size');
SizeAttributor.whitelist = SIZE_WHITELIST;
Quill.register(SizeAttributor, true);

// Custom toolbar buttons render as <button class="ql-{name}">. Quill expects
// either text content or an SVG inside via its `ui/icons` map.
const QuillIcons = Quill.import('ui/icons');
QuillIcons['size-increase'] =
  '<svg viewBox="0 0 18 18"><text x="2" y="14" font-size="14" font-weight="700" fill="currentColor">A</text><text x="11" y="9" font-size="9" fill="currentColor">+</text></svg>';
QuillIcons['size-decrease'] =
  '<svg viewBox="0 0 18 18"><text x="2" y="14" font-size="14" font-weight="700" fill="currentColor">A</text><text x="11" y="9" font-size="9" fill="currentColor">−</text></svg>';

const DEFAULT_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  [{ font: FONT_WHITELIST }],
  [{ size: SIZE_WHITELIST }],
  ['size-decrease', 'size-increase'],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'link'],
  ['clean']
];

// Detects whether a Quill HTML string actually has user content (Quill emits
// "<p><br></p>" for an empty editor).
const isEmptyQuill = (html) => {
  if (!html) return true;
  const stripped = html
    .replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
  return stripped.length === 0;
};

// Read the current selection's size (or the default 13px if unset) and apply
// the next step. We anchor the default to 13 because react-quill-new's editor
// inherits the parent's 0.875rem — keeping ±1 consistent regardless of theme.
const DEFAULT_PX = 13;

function bumpSize(quill, delta) {
  const range = quill.getSelection(true);
  if (!range) return;
  const format = quill.getFormat(range);
  const current = format.size && typeof format.size === 'string' ? parseInt(format.size, 10) : DEFAULT_PX;
  const next = Math.min(48, Math.max(8, current + delta));
  quill.format('size', `${next}px`, 'user');
}

export default function RichTextField({
  label,
  value,
  onChange,
  readOnly = false,
  minHeight = 120,
  placeholder,
  toolbar = DEFAULT_TOOLBAR
}) {
  const theme = useTheme();
  const html = value || '';
  const quillRef = useRef(null);

  // Build the modules config once per render so the handlers always see the
  // current Quill instance via the closure.
  const modules = useMemo(
    () => ({
      toolbar: {
        container: toolbar,
        handlers: {
          'size-increase': function sizeIncrease() {
            bumpSize(this.quill, +1);
          },
          'size-decrease': function sizeDecrease() {
            bumpSize(this.quill, -1);
          }
        }
      }
    }),
    [toolbar]
  );

  if (readOnly) {
    return (
      <Box>
        {label && <FormLabel sx={{ display: 'block', fontSize: '0.75rem', mb: 0.5, color: 'text.secondary' }}>{label}</FormLabel>}
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            px: 1.5,
            py: 1,
            minHeight,
            bgcolor: alpha(theme.palette.text.primary, 0.03),
            color: 'text.primary',
            fontFamily: theme.typography.body2.fontFamily,
            fontSize: '0.875rem',
            lineHeight: 1.5,
            '& p': { m: 0, mb: 0.5 },
            '& p:last-child': { mb: 0 },
            '& ul, & ol': { pl: 3, m: 0, mb: 0.5 },
            '& h1': { fontSize: '1.25rem', m: 0, mb: 0.5 },
            '& h2': { fontSize: '1.125rem', m: 0, mb: 0.5 },
            '& h3': { fontSize: '1rem', m: 0, mb: 0.5 }
          }}
        >
          {isEmptyQuill(html) ? (
            <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              —
            </Box>
          ) : (
            <Box component="div" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </Box>
      </Box>
    );
  }

  // CSS overrides so each font in the dropdown previews in its own family —
  // matches Word's behavior where you see the letterform before picking.
  const fontPickerStyles = FONT_WHITELIST.reduce((acc, font) => {
    acc[`& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="${font}"]::before`] = { content: `"${font}"`, fontFamily: font };
    acc[`& .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="${font}"]::before`] = { content: `"${font}"`, fontFamily: font };
    return acc;
  }, {});

  const sizePickerStyles = SIZE_WHITELIST.reduce((acc, size) => {
    acc[`& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${size}"]::before`] = { content: `"${size.replace('px', '')}"` };
    acc[`& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${size}"]::before`] = { content: `"${size.replace('px', '')}"` };
    return acc;
  }, {});

  return (
    <Box>
      {label && <FormLabel sx={{ display: 'block', fontSize: '0.75rem', mb: 0.5, color: 'text.secondary' }}>{label}</FormLabel>}
      <Box
        sx={{
          '& .quill': {
            bgcolor: 'background.paper',
            borderRadius: 1
          },
          '& .ql-toolbar': {
            borderColor: `${theme.palette.divider} !important`,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            flexWrap: 'wrap'
          },
          // Make the font picker wide enough to fit "Times New Roman".
          '& .ql-snow .ql-picker.ql-font': { width: 140 },
          '& .ql-snow .ql-picker.ql-size': { width: 60 },
          ...fontPickerStyles,
          ...sizePickerStyles,
          '& .ql-container': {
            borderColor: `${theme.palette.divider} !important`,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            fontFamily: theme.typography.body1.fontFamily,
            fontSize: '0.875rem'
          },
          '& .ql-editor': { minHeight }
        }}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={html}
          onChange={(next) => onChange?.(isEmptyQuill(next) ? '' : next)}
          modules={modules}
          placeholder={placeholder}
        />
      </Box>
    </Box>
  );
}

RichTextField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  minHeight: PropTypes.number,
  placeholder: PropTypes.string,
  toolbar: PropTypes.array
};
